import React, { useState } from 'react';
import { Alert, Button, Spinner, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import { getSmartRedirectPath, clearAccountCache } from '../shared/accountCache';

export default function EmailVerificationCodeRequired({ currentUser, onShowContactModal }) {
  const { t } = useTranslation();
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [verifySuccess, setVerifySuccess] = useState(false);

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendMessage('');
    setResendError('');
    setVerifyError('');
    
    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.RESEND_VERIFICATION}`,
        {},
        'POST'
      );
      
      if (response.success) {
        setResendMessage(t('auth.emailVerification.emailSent'));
        setVerificationCode('');
      } else {
        setResendError(response.message || t('auth.emailVerification.resendFailed'));
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      setResendError(t('auth.emailVerification.errorOccurred'));
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      setVerifyError(t('auth.emailVerification.invalidCode'));
      return;
    }

    setIsVerifying(true);
    setVerifyError('');
    setResendMessage('');
    setResendError('');

    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.VERIFY_EMAIL_CODE}`,
        { code: verificationCode },
        'POST'
      );

      if (response.success) {
        // Show success UI (same as token verification)
        setVerifySuccess(true);
        
        // Update user in localStorage (same as token verification)
        const updatedUser = { ...currentUser, emailVerified: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Invalidate cache and redirect after delay (same as token verification)
        clearAccountCache();
        setTimeout(async () => {
          const redirectPath = await getSmartRedirectPath(updatedUser);
          window.location.href = redirectPath;
        }, 500);
      } else {
        setVerifyError(response.message || t('auth.emailVerification.verificationFailed'));
        setIsVerifying(false);
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setVerifyError(t('auth.emailVerification.errorOccurred'));
      setIsVerifying(false);
    }
  };

  // Show success UI (same as token verification)
  if (verifySuccess) {
    return (
      <>
        <div className="mb-4">
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
        </div>
        <h3 className="mb-3">{t('auth.verifyEmail.successTitle')}</h3>
        <p className="text-muted mb-4">
          {t('auth.verifyEmail.successMessageLoggedIn')}
          <br />
          <Spinner animation="border" size="sm" className="mt-2 me-2" />
          {t('auth.verifyEmail.redirecting')}
        </p>
      </>
    );
  }

  return (
    <>
      <div className="mb-3">
        <p className="text-muted">
          {t('auth.emailVerification.codeInstructions')}
        </p>
      </div>

      <Form onSubmit={handleVerifyCode} className="mb-3">
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
            placeholder={t('auth.emailVerification.enterCode')}
            className="text-center fs-4"
            style={{ letterSpacing: '0.5em' }}
            disabled={isVerifying}
          />
        </Form.Group>

        {verifyError && (
          <Alert variant="danger" dismissible onClose={() => setVerifyError('')} className="mb-3">
            {verifyError}
          </Alert>
        )}

        {resendMessage && (
          <Alert variant="success" dismissible onClose={() => setResendMessage('')} className="mb-3">
            <i className="bi bi-check-circle-fill me-2"></i>
            {resendMessage}
          </Alert>
        )}

        {resendError && (
          <Alert variant="danger" dismissible onClose={() => setResendError('')} className="mb-3">
            {resendError}
          </Alert>
        )}

        <div className="d-grid gap-2">
          <Button 
            variant="primary" 
            size="lg"
            type="submit"
            disabled={isVerifying || verificationCode.length !== 6}
          >
            {isVerifying ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('auth.emailVerification.verifying')}
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                {t('auth.emailVerification.verifyCode')}
              </>
            )}
          </Button>
          <Button 
            variant="outline-secondary"
            onClick={handleResendVerification}
            disabled={isResending || isVerifying}
          >
            {isResending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('auth.emailVerification.sending')}
              </>
            ) : (
              <>
                <i className="bi bi-envelope me-2"></i>
                {t('auth.emailVerification.resendCode')}
              </>
            )}
          </Button>
        </div>
      </Form>

      {/* Contact us section */}
      <hr className="my-3" />
      <p className="text-muted small mb-2">
        {t('auth.emailVerification.didntReceiveEmail')}
      </p>
      <Button 
        variant="outline-secondary"
        size="sm"
        onClick={onShowContactModal}
      >
        <i className="bi bi-chat-dots me-2"></i>
        {t('auth.emailVerification.contactUs')}
      </Button>
    </>
  );
}
